/** Pagina "Mas" como utilidades de cuenta y ajustes (design.md §22.1). Sin duplicados operativos. */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileCard } from '@/components/MobileCard';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useMobileAuth } from '@/state/MobileAuthContext';
import { pushService } from '@/services/pushService';
import type { MobileIconName } from '@/components/MobileIcon';
import packageJson from '../../../package.json';

type UtilityLink = {
  key: string;
  to: string;
  label: string;
  description: string;
  icon: MobileIconName;
  iconColor: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  permissionCode?: string;
};

/** Enlaces de cuenta: visibles siempre para la sesion activa, salvo permiso indicado. */
const ACCOUNT_LINKS: UtilityLink[] = [
  {
    key: 'profile',
    to: '/m/profile',
    label: 'Mi perfil',
    description: 'Nombre, correo y empresa activa',
    icon: 'person',
    iconColor: 'primary',
  },
];

const APP_NAME = 'ERPA Móvil';
const APP_VERSION = typeof packageJson.version === 'string' ? packageJson.version : '';

export function MorePage() {
  const { hasPermission, membershipsCount, logout } = useMobileAuth();
  const [pushMessage, setPushMessage] = useState<string | null>(null);
  const [pushOk, setPushOk] = useState<boolean | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  const visibleLinks = ACCOUNT_LINKS.filter(
    (item) => !item.permissionCode || hasPermission(item.permissionCode),
  );
  const showSwitch = membershipsCount > 1;
  const pushSupported = pushService.supported();

  async function enablePush(): Promise<void> {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      await pushService.subscribe();
      setPushMessage('Avisos activados correctamente');
      setPushOk(true);
    } catch (error) {
      setPushMessage(error instanceof Error ? error.message : 'No se pudo activar');
      setPushOk(false);
    } finally {
      setPushBusy(false);
    }
  }

  return (
    <MobileLayout title="Más" subtitle="Cuenta y ajustes">
      <div className="space-y-3">
        {visibleLinks.map((item) => (
          <Link key={item.key} to={item.to} className="block">
            <MobileCard
              variant="interactive"
              title={item.label}
              description={item.description}
              icon={item.icon}
              iconColor={item.iconColor}
            />
          </Link>
        ))}

        {showSwitch && (
          <Link to="/m/company" className="block">
            <MobileCard
              variant="interactive"
              title="Cambiar de empresa"
              description="Elige la empresa activa"
              icon="swap_horiz"
              iconColor="info"
            />
          </Link>
        )}

        {pushSupported && (
          <MobileCard
            variant="interactive"
            title="Activar avisos de tareas"
            description={
              pushBusy
                ? 'Activando avisos…'
                : (pushMessage ?? 'Recibe un aviso cuando tengas una tarea nueva')
            }
            icon={pushOk === true ? 'check_circle' : pushOk === false ? 'error' : 'notifications_active'}
            iconColor={pushOk === true ? 'success' : pushOk === false ? 'error' : 'warning'}
            onClick={() => void enablePush()}
          />
        )}

        <MobileCard
          variant="default"
          title="Acerca de"
          description={APP_VERSION ? `${APP_NAME} · versión ${APP_VERSION}` : APP_NAME}
          icon="info"
          iconColor="info"
        />

        <MobileCard
          variant="interactive"
          title="Cerrar sesión"
          description="Salir de la aplicación en este dispositivo"
          icon="logout"
          iconColor="error"
          onClick={() => setConfirmLogout(true)}
        />
      </div>

      {confirmLogout && (
        <ConfirmDialog
          title="¿Cerrar sesión?"
          message="Se cierra la sesión actual en este dispositivo."
          confirmLabel="Cerrar sesión"
          onConfirm={() => void logout()}
          onCancel={() => setConfirmLogout(false)}
        />
      )}
    </MobileLayout>
  );
}
